// src/components/RhinoVOIPCallDetailRecords.jsx
import React, { useState, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import moment from 'moment-timezone';
import {
  Button,
  DateRangeDropdown,
  Dropdown,
  DropdownMenuItem,
  DropdownMenuItemWild,
  Icon,
  SmartTable,
  UtilityInlineGrid,
} from 'rhinostyle';
import { useGetCdrsQuery } from '../services/VoipService';
import { useVoip } from '../voip/VoipProvider';
import PageLoader from './PageLoader';
import EmptyMessage from './EmptyMessage';
import SmartTableHeader from './SmartTableHeader';
import ContactSidePanel from './ContactSidePanel';
import { formatStatus, formatDuration, formatPhone } from '../helpers/VoipHelpers';
import RhinoVoipVoicemailPlayer from './RhinoVoipVoicemailPlayer';
import callDetailsIcon from '../assets/images/voip/call-details-icon.png';
import contactCardIcon from '../assets/images/voip/contact-card-icon.svg';
import voicemailIcon from '../assets/images/voip/voicemail-icon.png';

const PAGE_SIZE_OPTIONS = [25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;

const CDR_HEADERS_CONFIG = {
  caller_id_number: { default: false, direction: -1 },
  destination_number: { default: false, direction: -1 },
  direction: { default: false, direction: -1 },
  status: { default: false, direction: -1 },
  start_stamp: { default: true, direction: -1 },
  duration: { default: false, direction: -1 },
};

const DATE_PICKER_LABELS = [
  { id: 1, label: 'Yesterday', duration: 1 },
  { id: 2, label: 'Last 7 Days', duration: 6 },
  { id: 3, label: 'Last 30 Days', duration: 29 },
  { id: 4, label: 'Last 90 Days', duration: 89 },
  { id: 5, label: 'Last 12 Months', duration: 'year' },
  { id: 6, label: 'Custom Date' },
];

const DIRECTION_OPTIONS = [
  { id: 'inbound', value: 'Inbound' },
  { id: 'outbound', value: 'Outbound' },
  { id: 'local', value: 'Internal' },
];

const STATUS_OPTIONS = [
  { id: 'answered', value: 'Answered' },
  { id: 'missed', value: 'Missed' },
  { id: 'no_answer', value: 'No Answer' },
  { id: 'voicemail', value: 'Voicemail' },
  { id: 'outbound', value: 'Outbound' },
  { id: 'local', value: 'Internal' },
  { id: 'busy', value: 'Busy' },
  { id: 'failed', value: 'Failed' },
];

const RhinoVOIPCallDetailRecords = () => {
  const { call: placeCall } = useVoip();
  const [sidePanelPhone, setSidePanelPhone] = useState(null);
  const [detailRecord, setDetailRecord] = useState(null);
  const [voicemailRecord, setVoicemailRecord] = useState(null);
  const [confirmCall, setConfirmCall] = useState(null);
  const [startDate, setStartDate] = useState(moment().subtract(6, 'days'));
  const [endDate, setEndDate] = useState(moment());
  const [activeKey, setActiveKey] = useState(2);
  const [selectedDirections, setSelectedDirections] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [selectedRingGroups, setSelectedRingGroups] = useState([]);
  const [selectedIvrs, setSelectedIvrs] = useState([]);
  const [pageNumber, setPageNumber] = useState(0);
  const [displayPageSize, setDisplayPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [filterResetKey, setFilterResetKey] = useState(0);
  const [closeCounters, setCloseCounters] = useState({ direction: 0, status: 0, member: 0, contact: 0, ringGroup: 0, ivr: 0 });
  const [memberSearch, setMemberSearch] = useState('');
  const [contactSearch, setContactSearch] = useState('');
  const [ringGroupSearch, setRingGroupSearch] = useState('');
  const [ivrSearch, setIvrSearch] = useState('');

  const handleDropdownOpen = (name) => {
    setCloseCounters((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => { if (k !== name) next[k] += 1; });
      return next;
    });
  };

  const buildFilters = useCallback(() => {
    // Send UTC instants derived from the user's local-day window so the
    // backend's UTC-stored stamps don't drop late-evening calls onto the
    // next UTC day. Date-only strings get parsed at UTC midnight, which
    // misses anything past local ~7pm EST / ~4pm PST.
    const filters = {
      start_stamp: startDate.clone().startOf('day').toISOString(),
      end_stamp: endDate.clone().endOf('day').toISOString(),
    };
    if (selectedDirections.length > 0) filters.direction = selectedDirections.join(',');
    if (selectedStatuses.length > 0) filters.status = selectedStatuses.join(',');
    return filters;
  }, [startDate, endDate, selectedDirections, selectedStatuses]);

  const { data, isLoading, isFetching } = useGetCdrsQuery(buildFilters());

  const allRecords = Array.isArray(data?.objects) ? data.objects : [];

  const STAR_CODE_LABELS = { '*97': 'Voicemail', '*411': 'Company Directory' };

  // Resolve member keys (extensions + star codes)
  const getMemberKeys = (r) => {
    const keys = [];
    if (r.caller_extension) {
      keys.push(String(r.caller_extension));
    }
    if (r.dest_extension) {
      keys.push(String(r.dest_extension));
    }
    if (STAR_CODE_LABELS[r.destination_number]) {
      keys.push(`star:${r.destination_number}`);
    }
    return keys;
  };

  // Resolve contact keys (external numbers, either side)
  const getContactKeys = (r) => {
    const keys = [];
    if (!r.caller_extension && r.caller_id_number) {
      keys.push(r.caller_contact_name || r.caller_id_number);
    }
    if (!r.dest_extension && !r.ring_group_name && !STAR_CODE_LABELS[r.destination_number] && r.destination_number) {
      keys.push(r.destination_contact_name || r.destination_number);
    }
    return keys;
  };

  // Derive distinct Member options (extensions + star codes)
  const memberOptions = useMemo(() => {
    const seen = new Set();
    const acc = [];
    allRecords.forEach((r) => {
      if (r.caller_extension) {
        const key = String(r.caller_extension);
        if (!seen.has(key)) {
          seen.add(key);
          const name = r.caller_extension_name || r.caller_id_name || key;
          acc.push({ id: key, value: `${name} (Ext. ${key})` });
        }
      }
      if (r.dest_extension) {
        const key = String(r.dest_extension);
        if (!seen.has(key)) {
          seen.add(key);
          const name = r.dest_name || key;
          acc.push({ id: key, value: `${name} (Ext. ${key})` });
        }
      }
      const starLabel = STAR_CODE_LABELS[r.destination_number];
      if (starLabel) {
        const key = `star:${r.destination_number}`;
        if (!seen.has(key)) {
          seen.add(key);
          acc.push({ id: key, value: starLabel });
        }
      }
    });
    return acc.sort((a, b) => a.value.localeCompare(b.value));
  }, [allRecords]);

  // Derive distinct Ring Group options
  const ringGroupOptions = useMemo(() => {
    const seen = new Set();
    const acc = [];
    allRecords.forEach((r) => {
      if (r.ring_group_name && !seen.has(r.ring_group_name)) {
        seen.add(r.ring_group_name);
        acc.push({ id: r.ring_group_name, value: r.ring_group_extension ? `${r.ring_group_name} (${r.ring_group_extension})` : r.ring_group_name });
      }
    });
    return acc.sort((a, b) => a.value.localeCompare(b.value));
  }, [allRecords]);

  // Derive distinct Contact options (external numbers, either side)
  const contactOptions = useMemo(() => {
    const seen = new Set();
    const acc = [];
    allRecords.forEach((r) => {
      if (!r.caller_extension && r.caller_id_number) {
        const key = r.caller_contact_name || r.caller_id_number;
        if (!seen.has(key)) {
          seen.add(key);
          const label = r.caller_contact_name
            ? `${r.caller_contact_name} - ${formatPhone(r.caller_id_number)}`
            : formatPhone(r.caller_id_number);
          acc.push({ id: key, value: label });
        }
      }
      if (!r.dest_extension && !r.ring_group_name && !STAR_CODE_LABELS[r.destination_number] && r.destination_number) {
        const key = r.destination_contact_name || r.destination_number;
        if (!seen.has(key)) {
          seen.add(key);
          const label = r.destination_contact_name
            ? `${r.destination_contact_name} - ${formatPhone(r.destination_number)}`
            : formatPhone(r.destination_number);
          acc.push({ id: key, value: label });
        }
      }
    });
    return acc.sort((a, b) => a.value.localeCompare(b.value));
  }, [allRecords]);

  // Derive distinct IVR options
  const ivrOptions = useMemo(() => {
    const seen = new Set();
    const acc = [];
    allRecords.forEach((r) => {
      if (r.ivr_menu_name && !seen.has(r.ivr_menu_name)) {
        seen.add(r.ivr_menu_name);
        acc.push({ id: r.ivr_menu_name, value: r.ivr_menu_name });
      }
    });
    return acc.sort((a, b) => a.value.localeCompare(b.value));
  }, [allRecords]);

  const filteredRecords = useMemo(() => {
    let filtered = allRecords;
    if (selectedMembers.length > 0) {
      filtered = filtered.filter((r) => getMemberKeys(r).some((k) => selectedMembers.includes(k)));
    }
    if (selectedRingGroups.length > 0) {
      filtered = filtered.filter((r) => r.ring_group_name && selectedRingGroups.includes(r.ring_group_name));
    }
    if (selectedIvrs.length > 0) {
      filtered = filtered.filter((r) => r.ivr_menu_name && selectedIvrs.includes(r.ivr_menu_name));
    }
    if (selectedContacts.length > 0) {
      filtered = filtered.filter((r) => getContactKeys(r).some((k) => selectedContacts.includes(k)));
    }
    return filtered;
  }, [allRecords, selectedMembers, selectedRingGroups, selectedIvrs, selectedContacts]);

  const totalFilteredCount = filteredRecords.length;
  const totalPages = Math.ceil(totalFilteredCount / displayPageSize);
  const paginatedRecords = filteredRecords.slice(pageNumber * displayPageSize, (pageNumber + 1) * displayPageSize);

  // --- Filter handlers ---

  const selectDate = (date) => {
    if (date && date.startDate && date.endDate) {
      setStartDate(moment(date.startDate));
      setEndDate(moment(date.endDate));
      setActiveKey(date.activeKey);
      setPageNumber(0);
    }
  };

  const calculateDateRange = (duration, selectedLabelIndex) => {
    let fromDate = moment().subtract(duration, 'days');
    let toDate = moment();

    if (selectedLabelIndex === 1) {
      toDate = moment().subtract(1, 'days');
    }
    if (duration === 'year') {
      fromDate = moment().subtract(1, 'years').add(1, 'days');
    }
    return { startDate: fromDate, endDate: toDate };
  };

  const toggleDirection = (value) => {
    setSelectedDirections((prev) =>
      (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
    setPageNumber(0);
  };

  const toggleStatus = (value) => {
    setSelectedStatuses((prev) =>
      (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
    setPageNumber(0);
  };

  const toggleMember = (value) => {
    setSelectedMembers((prev) =>
      (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
    setPageNumber(0);
  };

  const toggleContact = (value) => {
    setSelectedContacts((prev) =>
      (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
    setPageNumber(0);
  };

  const toggleRingGroup = (value) => {
    setSelectedRingGroups((prev) =>
      (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
    setPageNumber(0);
  };

  const toggleIvr = (value) => {
    setSelectedIvrs((prev) =>
      (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
    setPageNumber(0);
  };

  const clearAllFilters = () => {
    setStartDate(moment().subtract(6, 'days'));
    setEndDate(moment());
    setActiveKey(2);
    setSelectedDirections([]);
    setSelectedStatuses([]);
    setSelectedMembers([]);
    setSelectedContacts([]);
    setSelectedRingGroups([]);
    setSelectedIvrs([]);
    setMemberSearch('');
    setContactSearch('');
    setRingGroupSearch('');
    setIvrSearch('');
    setPageNumber(0);
    setFilterResetKey((prev) => prev + 1);
  };

  // --- Pagination ---

  const handlePagination = (direction) => {
    if (direction === 'next') {
      setPageNumber((prev) => prev + 1);
    } else if (direction === 'previous' && pageNumber > 0) {
      setPageNumber((prev) => prev - 1);
    }
  };

  const isPreviousDisabled = pageNumber === 0;
  const isNextDisabled = pageNumber >= totalPages - 1;
  const pageItemIndexFrom = totalFilteredCount > 0 ? pageNumber * displayPageSize + 1 : 0;
  const pageItemIndexTo = Math.min((pageNumber + 1) * displayPageSize, totalFilteredCount);

  // --- Formatters ---

  function formatDate(iso) {
    if (!iso) return '-';
    return moment(iso).local().format('MM/DD/YYYY h:mm A');
  }

  function formatCaller(record) {
    const num = record.caller_id_number;
    const contactName = record.caller_contact_name;
    const callerExtName = record.caller_extension_name;
    const callerExt = record.caller_extension;

    // For outbound calls, show the actual extension user who placed the call
    if (record.direction === 'outbound' && callerExtName && callerExt) {
      return `${callerExtName} (Ext. ${callerExt})`;
    }

    const showExt = callerExt && record.direction !== 'inbound';
    const displayName = contactName || record.caller_id_name;
    if (displayName && displayName !== num) {
      if (showExt) return `${displayName} (Ext. ${callerExt})`;
      return `${displayName} - ${formatPhone(num)}`;
    }
    if (showExt) return `Ext. ${callerExt}`;
    return formatPhone(num) || '-';
  }

  function formatDestination(record) {
    const num = record.destination_number;
    const destExt = record.dest_extension;
    const contactName = record.destination_contact_name;
    const rgName = record.ring_group_name;
    const displayName = contactName || record.dest_name;

    // Star code destinations
    if (STAR_CODE_LABELS[num]) return STAR_CODE_LABELS[num];

    // Ring group destination — show who answered if available, else fall back to the
    // ring group's own extension. Avoid leaking *99XX voicemail-deposit numbers.
    if (rgName) {
      let answeredBy;
      if (record.dest_name && destExt) {
        answeredBy = `${record.dest_name}, Ext. ${destExt}`;
      } else if (record.dest_name) {
        answeredBy = record.dest_name;
      } else {
        answeredBy = record.ring_group_extension || destExt || '';
      }
      return answeredBy ? `${rgName} (${answeredBy})` : rgName;
    }

    // BUG-027: If the IVR routed the call to a specific extension, show that ext with an IVR tag.
    // Only fall back to the bare IVR menu name when no downstream ext/ring group was reached.
    if (record.ivr_menu_name) {
      if (displayName && destExt) return `${displayName} (Ext. ${destExt}) via ${record.ivr_menu_name} (IVR)`;
      if (destExt) return `Ext. ${destExt} via ${record.ivr_menu_name} (IVR)`;
      return `${record.ivr_menu_name} (IVR)`;
    }

    // Resolved extension name (covers direct calls and *99 pickups)
    // Show "Ext." when destination resolved to an internal extension
    if (displayName && displayName !== num) {
      if (destExt) return `${displayName} (Ext. ${destExt})`;
      return `${displayName} - ${formatPhone(num)}`;
    }
    if (destExt) return `Ext. ${destExt}`;
    return formatPhone(num) || '-';
  }

  // --- SmartTable columns ---

  const [cdrHeadersConfig] = useState(CDR_HEADERS_CONFIG);

  const smartTableColumns = [
    {
      Header: () => (
        <SmartTableHeader headerName="From" sortKey="caller_id_number" sortDirection={cdrHeadersConfig.caller_id_number.direction} headers={cdrHeadersConfig} />
      ),
      headerStyle: { padding: 0 },
      accessor: 'caller_id_number',
      Cell: ({ original }) => formatCaller(original),
    },
    {
      Header: () => (
        <SmartTableHeader headerName="To" sortKey="destination_number" sortDirection={cdrHeadersConfig.destination_number.direction} headers={cdrHeadersConfig} />
      ),
      headerStyle: { padding: 0 },
      accessor: 'destination_number',
      Cell: ({ original }) => formatDestination(original),
    },
    {
      Header: () => (
        <SmartTableHeader headerName="Direction" sortKey="direction" sortDirection={cdrHeadersConfig.direction.direction} headers={cdrHeadersConfig} />
      ),
      headerStyle: { padding: 0 },
      accessor: 'direction',
      Cell: ({ value }) => (value === 'local' ? 'Internal' : formatStatus(value)),
    },
    {
      Header: () => (
        <SmartTableHeader headerName="Status" sortKey="status" sortDirection={cdrHeadersConfig.status.direction} headers={cdrHeadersConfig} />
      ),
      headerStyle: { padding: 0 },
      accessor: 'status',
      Cell: ({ value }) => formatStatus(value),
    },
    {
      Header: () => (
        <SmartTableHeader headerName="Start" sortKey="start_stamp" sortDirection={cdrHeadersConfig.start_stamp.direction} headers={cdrHeadersConfig} />
      ),
      headerStyle: { padding: 0 },
      accessor: 'start_stamp',
      Cell: ({ value }) => formatDate(value),
    },
    {
      Header: () => (
        <SmartTableHeader headerName="Duration" sortKey="duration" sortDirection={cdrHeadersConfig.duration.direction} headers={cdrHeadersConfig} />
      ),
      headerStyle: { padding: 0 },
      accessor: 'duration',
      Cell: ({ value }) => formatDuration(value),
    },
    {
      Header: 'Actions',
      accessor: 'xml_cdr_uuid',
      sortable: false,
      width: 160,
      Cell: ({ original }) => {
        const contactPhone = original.direction === 'inbound' ? original.caller_id_number : original.destination_number;
        // Show the Contact icon for any external phone number (10+ digits) so the side panel
        // can resolve the contact even when the backend pre-enrichment didn't match.
        const phoneDigits = (contactPhone || '').replace(/\D/g, '');
        const hasPhoneToLookUp = phoneDigits.length >= 10;
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <Button type="link" size="small" iconOnly onClick={() => setDetailRecord(original)} title="Call Details">
              <img src={callDetailsIcon} alt="Call Details" width="24" height="24" />
            </Button>
            {hasPhoneToLookUp && (
              <Button type="link" size="small" iconOnly onClick={() => setSidePanelPhone(contactPhone)} title="Contact">
                <img src={contactCardIcon} alt="Contact" width="24" height="24" />
              </Button>
            )}
            {original.voicemail_message_uuid && (
              <Button type="link" size="small" iconOnly onClick={() => setVoicemailRecord(original)} title="Play Voicemail">
                <img src={voicemailIcon} alt="Voicemail" width="24" height="24" />
              </Button>
            )}
            {/* Voicemail was recorded but the audio file isn't available
                (media_uploaded=false in the DB, usually meaning the audio
                never made it to storage). Surface this so users aren't
                left wondering why some VM rows have no play button. */}
            {original.voicemail_message && !original.voicemail_message_uuid && (
              <Button
                type="link"
                size="small"
                iconOnly
                disabled
                title="Audio unavailable — voicemail was recorded but never uploaded to storage"
              >
                <img
                  src={voicemailIcon}
                  alt="Voicemail audio unavailable"
                  width="24"
                  height="24"
                  style={{ opacity: 0.35 }}
                />
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  // --- Filter state checks ---

  const isDirectionSelected = selectedDirections.length > 0;
  const isStatusSelected = selectedStatuses.length > 0;
  const isMemberSelected = selectedMembers.length > 0;
  const isContactSelected = selectedContacts.length > 0;
  const isRingGroupSelected = selectedRingGroups.length > 0;
  const isIvrSelected = selectedIvrs.length > 0;
  const isAnyFilterSelected = isDirectionSelected || isStatusSelected || isMemberSelected || isContactSelected || isRingGroupSelected || isIvrSelected;

  const directionLabel = isDirectionSelected ? `${selectedDirections.length} Directions Selected` : 'Direction';
  const statusLabel = isStatusSelected ? `${selectedStatuses.length} Statuses Selected` : 'Status';
  const memberLabel = isMemberSelected ? `${selectedMembers.length} Members Selected` : 'Member';
  const contactLabel = isContactSelected ? `${selectedContacts.length} Contacts Selected` : 'Contact';
  const ringGroupLabel = isRingGroupSelected ? `${selectedRingGroups.length} Ring Groups Selected` : 'Ring Group';
  const ivrLabel = isIvrSelected ? `${selectedIvrs.length} IVRs Selected` : 'IVR';

  // --- Pagination controls ---

  const handlePageSizeChange = (name, value) => {
    setDisplayPageSize(Number(value));
    setPageNumber(0);
  };

  const renderPaginationControls = () => {
    const previousCaretClass = isPreviousDisabled ? 'pagination__caret--disabled' : '';
    const nextCaretClass = isNextDisabled ? 'pagination__caret--disabled' : '';
    return (
      <div className="u-text-right u-text-small cdr-records__pagination" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' }}>
        <span>Rows:</span>
        <select value={displayPageSize} onChange={(e) => handlePageSizeChange('displayPageSize', e.target.value)} style={{ padding: '2px 4px', fontSize: '13px' }}>
          {PAGE_SIZE_OPTIONS.map((size) => (
            <option key={size} value={size}>{size}</option>
          ))}
        </select>
        <span className="inbox-pagination__count">{`${pageItemIndexFrom}-${pageItemIndexTo} of ${totalFilteredCount}`}</span>
        <Button
          type="link"
          size="small"
          iconOnly
          disabled={isPreviousDisabled}
          onClick={() => handlePagination('previous')}
          className={previousCaretClass}
        >
          <Icon icon="caret-left" />
        </Button>
        <Button
          type="link"
          iconOnly
          size="small"
          disabled={isNextDisabled}
          onClick={() => handlePagination('next')}
          className={nextCaretClass}
        >
          <Icon icon="caret-right" />
        </Button>
      </div>
    );
  };

  // --- Render data ---

  const renderCdrData = () => {
    if (isLoading || isFetching) {
      return <PageLoader />;
    } else if (filteredRecords.length === 0) {
      return (
        <div className="cdr-records__nologs-wrapper">
          <EmptyMessage section="Call Manager" />
        </div>
      );
    }
    return (
      <>
        <SmartTable
          showPagination={false}
          data={paginatedRecords}
          columns={smartTableColumns}
          minRows={0}
          sortable
          striped
          sticky
          pageSize={paginatedRecords.length}
          defaultSorted={[{ id: 'start_stamp', desc: true }]}
          getTrProps={(state, rowInfo) => {
            const row = rowInfo?.original;
            if (!row) return {};
            const statusLower = row.status?.toLowerCase();
            const isInbound = row.direction === 'inbound';
            const redRow = statusLower === 'missed'
              || statusLower === 'no_answer'
              || (isInbound && statusLower === 'voicemail');
            return redRow ? { className: 'cdr-row-missed' } : {};
          }}
        />
        {totalFilteredCount > 0 && renderPaginationControls()}
      </>
    );
  };

  const isPaginationControlsVisible = totalFilteredCount > 0;

  return (
    <>
      <div className="app-page__container">
        <div className="app-panels">
          <div className="list-panel__wrapper">
            <div className="list-panel">
              <div className="app-page__header u-flex-direction-column">
                <div className="app-page__header__title cdr-records__title">Call Detail Records</div>
              </div>
              <div className="box responsive-table cdr-records__grid">
                <div className="cdr-records__filter-wrapper">
                  <div className="cdr-records__filter-label">
                    FILTER BY:
                    {isAnyFilterSelected && (
                      <Button data-cypress="clearAll" type="link" className="clear-all__button" onClick={clearAllFilters}>Clear All &nbsp;<Icon icon="close" /></Button>
                    )}
                  </div>
                  <div className="row">
                    <div>
                      <UtilityInlineGrid>
                        <DateRangeDropdown
                          name="CDR Date Picker"
                          startDate={startDate}
                          endDate={endDate}
                          selectDate={selectDate}
                          minDate={moment().subtract(1, 'year')}
                          maxDate={moment()}
                          activeKey={activeKey}
                          position="center"
                          dropdownMenuItems={DATE_PICKER_LABELS}
                          selectDateRange={calculateDateRange}
                          dataCypress="cdrDatePicker"
                          isCustomDate
                        />
                        <Dropdown
                          key={`direction-${filterResetKey}-${closeCounters.direction}`}
                          position="bottom"
                          activeKey={selectedDirections.length === 1 ? selectedDirections[0] : ''}
                          label={directionLabel}
                          type={isDirectionSelected ? 'primary' : 'input'}
                          outlined={!!isDirectionSelected}
                          name="cdrDirection"
                          onClick={() => handleDropdownOpen('direction')}
                          onSelect={toggleDirection}
                          dataCypress="cdrDirection"
                          manualClose
                        >
                          {DIRECTION_OPTIONS.map((item) => (
                            <DropdownMenuItem
                              key={item.id}
                              id={item.id}
                              label={item.value}
                              icon={selectedDirections.includes(item.id) ? 'checkmark' : ''}
                            />
                          ))}
                        </Dropdown>
                        <Dropdown
                          key={`status-${filterResetKey}-${closeCounters.status}`}
                          position="bottom"
                          activeKey={selectedStatuses.length === 1 ? selectedStatuses[0] : ''}
                          label={statusLabel}
                          type={isStatusSelected ? 'primary' : 'input'}
                          outlined={!!isStatusSelected}
                          name="cdrStatus"
                          onClick={() => handleDropdownOpen('status')}
                          onSelect={toggleStatus}
                          dataCypress="cdrStatus"
                          manualClose
                        >
                          {STATUS_OPTIONS.map((item) => (
                            <DropdownMenuItem
                              key={item.id}
                              id={item.id}
                              label={item.value}
                              icon={selectedStatuses.includes(item.id) ? 'checkmark' : ''}
                            />
                          ))}
                        </Dropdown>
                        <Dropdown
                          key={`member-${filterResetKey}-${closeCounters.member}`}
                          position="bottom"
                          activeKey={selectedMembers.length === 1 ? selectedMembers[0] : ''}
                          label={memberLabel}
                          type={isMemberSelected ? 'primary' : 'input'}
                          outlined={!!isMemberSelected}
                          name="cdrMember"
                          onClick={() => handleDropdownOpen('member')}
                          onSelect={toggleMember}
                          dataCypress="cdrMember"
                          manualClose
                        >
                          <DropdownMenuItemWild>
                            <input
                              type="text"
                              className="form__control"
                              style={{ minWidth: '180px' }}
                              placeholder="Search..."
                              value={memberSearch}
                              onChange={(e) => setMemberSearch(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </DropdownMenuItemWild>
                          {memberOptions
                            .filter((item) => !memberSearch || item.value.toLowerCase().includes(memberSearch.toLowerCase()))
                            .map((item) => (
                              <DropdownMenuItem
                                key={item.id}
                                id={item.id}
                                label={item.value}
                                icon={selectedMembers.includes(item.id) ? 'checkmark' : ''}
                              />
                            ))}
                        </Dropdown>
                        <Dropdown
                          key={`contact-${filterResetKey}-${closeCounters.contact}`}
                          position="bottom"
                          activeKey={selectedContacts.length === 1 ? selectedContacts[0] : ''}
                          label={contactLabel}
                          type={isContactSelected ? 'primary' : 'input'}
                          outlined={!!isContactSelected}
                          name="cdrContact"
                          onClick={() => handleDropdownOpen('contact')}
                          onSelect={toggleContact}
                          dataCypress="cdrContact"
                          manualClose
                        >
                          <DropdownMenuItemWild>
                            <input
                              type="text"
                              className="form__control"
                              style={{ minWidth: '180px' }}
                              placeholder="Search..."
                              value={contactSearch}
                              onChange={(e) => setContactSearch(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </DropdownMenuItemWild>
                          {contactOptions
                            .filter((item) => !contactSearch || item.value.toLowerCase().includes(contactSearch.toLowerCase()))
                            .map((item) => (
                              <DropdownMenuItem
                                key={item.id}
                                id={item.id}
                                label={item.value}
                                icon={selectedContacts.includes(item.id) ? 'checkmark' : ''}
                              />
                            ))}
                        </Dropdown>
                        <Dropdown
                          key={`ringGroup-${filterResetKey}-${closeCounters.ringGroup}`}
                          position="bottom"
                          activeKey={selectedRingGroups.length === 1 ? selectedRingGroups[0] : ''}
                          label={ringGroupLabel}
                          type={isRingGroupSelected ? 'primary' : 'input'}
                          outlined={!!isRingGroupSelected}
                          name="cdrRingGroup"
                          onClick={() => handleDropdownOpen('ringGroup')}
                          onSelect={toggleRingGroup}
                          dataCypress="cdrRingGroup"
                          manualClose
                        >
                          <DropdownMenuItemWild>
                            <input
                              type="text"
                              className="form__control"
                              style={{ minWidth: '180px' }}
                              placeholder="Search..."
                              value={ringGroupSearch}
                              onChange={(e) => setRingGroupSearch(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </DropdownMenuItemWild>
                          {ringGroupOptions
                            .filter((item) => !ringGroupSearch || item.value.toLowerCase().includes(ringGroupSearch.toLowerCase()))
                            .map((item) => (
                              <DropdownMenuItem
                                key={item.id}
                                id={item.id}
                                label={item.value}
                                icon={selectedRingGroups.includes(item.id) ? 'checkmark' : ''}
                              />
                            ))}
                        </Dropdown>
                        <Dropdown
                          key={`ivr-${filterResetKey}-${closeCounters.ivr}`}
                          position="bottom"
                          activeKey={selectedIvrs.length === 1 ? selectedIvrs[0] : ''}
                          label={ivrLabel}
                          type={isIvrSelected ? 'primary' : 'input'}
                          outlined={!!isIvrSelected}
                          name="cdrIvr"
                          onClick={() => handleDropdownOpen('ivr')}
                          onSelect={toggleIvr}
                          dataCypress="cdrIvr"
                          manualClose
                        >
                          <DropdownMenuItemWild>
                            <input
                              type="text"
                              className="form__control"
                              style={{ minWidth: '180px' }}
                              placeholder="Search..."
                              value={ivrSearch}
                              onChange={(e) => setIvrSearch(e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                            />
                          </DropdownMenuItemWild>
                          {ivrOptions
                            .filter((item) => !ivrSearch || item.value.toLowerCase().includes(ivrSearch.toLowerCase()))
                            .map((item) => (
                              <DropdownMenuItem
                                key={item.id}
                                id={item.id}
                                label={item.value}
                                icon={selectedIvrs.includes(item.id) ? 'checkmark' : ''}
                              />
                            ))}
                        </Dropdown>
                      </UtilityInlineGrid>
                    </div>
                    <div className="cdr-records__pagination-wrapper">
                      {isPaginationControlsVisible && renderPaginationControls()}
                    </div>
                  </div>
                </div>
                {renderCdrData()}
              </div>
            </div>
          </div>
        </div>
      </div>
      <ContactSidePanel phoneNumber={sidePanelPhone} onClose={() => setSidePanelPhone(null)} />
      {detailRecord && ReactDOM.createPortal(
        <div className="cdr-modal-overlay" onClick={() => setDetailRecord(null)}>
          <div className="cdr-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', maxHeight: '80vh', overflow: 'auto' }}>
            <div className="cdr-modal-title">CDR Details</div>
            <div className="cdr-modal-body">
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <tbody>
                  {Object.entries(detailRecord).map(([key, val]) => (
                    <tr key={key} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '4px 8px', fontWeight: 600, whiteSpace: 'nowrap', verticalAlign: 'top' }}>{key}</td>
                      <td style={{ padding: '4px 8px', wordBreak: 'break-all' }}>{val === null || val === undefined ? '-' : String(val)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="cdr-modal-actions">
              <button type="button" className="cdr-modal-cancel" onClick={() => setDetailRecord(null)}>Close</button>
            </div>
          </div>
        </div>,
        document.body,
      )}
      {voicemailRecord && ReactDOM.createPortal(
        <div className="cdr-modal-overlay" onClick={() => setVoicemailRecord(null)}>
          <div className="cdr-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '450px' }}>
            <div className="cdr-modal-title">Voicemail</div>
            <div className="cdr-modal-body" style={{ padding: '16px 24px' }}>
              <div style={{ fontSize: '13px', marginBottom: '12px', color: '#555' }}>
                <div>From: {voicemailRecord.caller_contact_name || formatPhone(voicemailRecord.caller_id_number)}</div>
                <div>Phone: {formatPhone(voicemailRecord.caller_id_number)}</div>
                <div>Date: {moment(voicemailRecord.start_stamp).local().format('MM/DD/YYYY h:mm A')}</div>
                <div>Duration: {formatDuration(voicemailRecord.voicemail_duration || voicemailRecord.duration)}</div>
                <div style={{ marginTop: '8px', fontStyle: 'italic' }}>{voicemailRecord.voicemail_transcription || 'No Transcription Available'}</div>
              </div>
              <RhinoVoipVoicemailPlayer
                voicemailMessageId={voicemailRecord.voicemail_message_uuid}
                callerIdNumber={voicemailRecord.caller_id_number}
              />
            </div>
            <div className="cdr-modal-actions">
              <button
                type="button"
                className="cdr-modal-confirm"
                onClick={() => {
                  const number = voicemailRecord.caller_id_number;
                  const label = voicemailRecord.caller_contact_name || formatPhone(number);
                  setVoicemailRecord(null);
                  setConfirmCall({ targetNumber: number, label });
                }}
              >
                Call Back
              </button>
              <button type="button" className="cdr-modal-cancel" onClick={() => setVoicemailRecord(null)}>Close</button>
            </div>
          </div>
        </div>,
        document.body,
      )}
      {confirmCall && ReactDOM.createPortal(
        <div className="cdr-modal-overlay">
          <div className="cdr-modal">
            <div className="cdr-modal-title">Confirm Call</div>
            <div className="cdr-modal-body">
              {confirmCall.label === confirmCall.targetNumber
                ? `Call ${formatPhone(confirmCall.targetNumber)}?`
                : `Call ${confirmCall.label} at ${formatPhone(confirmCall.targetNumber)}?`}
            </div>
            <div className="cdr-modal-actions">
              <button type="button" className="cdr-modal-cancel" onClick={() => setConfirmCall(null)}>Cancel</button>
              <button
                type="button"
                className="cdr-modal-confirm"
                onClick={() => {
                  placeCall(confirmCall.targetNumber);
                  setConfirmCall(null);
                }}
              >
                Yes
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export default RhinoVOIPCallDetailRecords;
